import { useState, useEffect } from 'react';
import { Card, Button, Badge, Input, Select, Icon } from '@atoms/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { Toast } from '@atoms/index';
import { StockUpdateModal, ConfirmModal } from '@organisms/index';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';
import type { Category, Product } from '@services/menuService';
import CategoryFormModal from '@organisms/CategoryFormModal';
import ProductFormModal from '@organisms/ProductFormModal';
import { ModifierGroupManagement } from '@organisms/ModifierGroupManagement';
import { OptionGroupManagement } from '@organisms/OptionGroupManagement';

type TabType = 'products' | 'modifiers' | 'categories';

const MenuTab = () => {
  const {
    categories,
    products,
    isLoadingCategories,
    isLoadingProducts,
    error,
    loadCategories,
    loadProducts,
    createCategory,
    updateCategory,
    deleteCategory,
    createProduct,
    updateProduct,
    deleteProduct,
    toggleProductAvailability,
    clearError,
  } = useRestaurantStore();

  const [activeTab, setActiveTab] = useState<TabType>('products');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [stockProduct, setStockProduct] = useState<Product | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [isDeleteCategoryLoading, setIsDeleteCategoryLoading] = useState(false);
  // Option group slide-over: stores the product whose option groups are being edited
  const [optionGroupProduct, setOptionGroupProduct] = useState<Product | null>(null);

  useEffect(() => {
    loadCategories();
    loadProducts();
  }, []);

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategoryClick = (categoryId: string) => {
    setCategoryToDelete(categoryId);
  };

  const handleDeleteCategoryConfirm = async () => {
    if (!categoryToDelete) return;
    setIsDeleteCategoryLoading(true);
    try {
      await deleteCategory(categoryToDelete);
      setCategoryToDelete(null);
      setToast({ message: 'Kategori silindi', type: 'success' });
    } catch (err) {
      setToast({ message: 'Kategori silinirken bir hata oluştu', type: 'error' });
    } finally {
      setIsDeleteCategoryLoading(false);
    }
  };

  const handleCreateProduct = (categoryId?: string) => {
    setSelectedProduct(null);
    setSelectedCategory(categoryId ? categories.find((c) => c.id === categoryId) || null : null);
    setIsProductModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedCategory(categories.find((c) => c.id === product.categoryId) || null);
    setIsProductModalOpen(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;

    try {
      await deleteProduct(productId);
      await loadProducts();
      setToast({ message: 'Ürün silindi', type: 'success' });
    } catch (err) {
      setToast({ message: 'Ürün silinirken bir hata oluştu', type: 'error' });
    }
  };

  const handleToggleAvailability = async (productId: string, isAvailable: boolean) => {
    try {
      await toggleProductAvailability(productId, !isAvailable);
      setToast({
        message: isAvailable ? 'Ürün kullanılamaz olarak işaretlendi' : 'Ürün kullanılabilir olarak işaretlendi',
        type: 'success',
      });
    } catch (err) {
      setToast({ message: 'Ürün durumu güncellenirken bir hata oluştu', type: 'error' });
    }
  };

  const handleOpenStockModal = (product: Product) => {
    setStockProduct(product);
    setIsStockModalOpen(true);
  };

  const handleUpdateStock = async (stockQuantity: number | null, lowStockThreshold: number | null) => {
    if (!stockProduct) return;
    try {
      const { updateProductStock } = useRestaurantStore.getState();
      await updateProductStock(stockProduct.id, stockQuantity, lowStockThreshold);
      setToast({ message: 'Stok başarıyla güncellendi', type: 'success' });
      setIsStockModalOpen(false);
      setStockProduct(null);
      loadProducts();
    } catch (err) {
      setToast({ message: 'Stok güncellenirken bir hata oluştu', type: 'error' });
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = !searchQuery.trim() || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategoryFilter === 'all' || product.categoryId === selectedCategoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const productsByCategory = categories.map((category) => ({
    category,
    products: filteredProducts.filter((p) => p.categoryId === category.id),
  })).filter(({ products }) => products.length > 0);

  const tabs = [
    { id: 'products' as TabType, label: 'Ana Ürünler', icon: 'cube' as const, count: products.length },
    { id: 'modifiers' as TabType, label: 'Yan Ürünler', icon: 'plus' as const, count: null },
    { id: 'categories' as TabType, label: 'Kategoriler', icon: 'folder' as const, count: categories.length },
  ];

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Menü Yönetimi</h2>
          <p className="text-sm text-gray-500 mt-0.5">Kategoriler, ürünler ve yan ürünlerinizi tek yerden yönetin</p>
        </div>
      </div>

      <div className="bg-gray-100/80 rounded-2xl p-1.5 inline-flex gap-1 flex-wrap">
        <nav className="flex gap-1" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchQuery('');
                setSelectedCategoryFilter('all');
              }}
              className={cn(
                'flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
              )}
            >
              <Icon name={tab.icon} size={18} className="flex-shrink-0" />
              <span>{tab.label}</span>
              {tab.count !== null && (
                <span
                  className={cn(
                    'min-w-[1.25rem] h-5 px-1.5 rounded-md text-xs font-bold flex items-center justify-center',
                    activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-gray-200/80 text-gray-600'
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <Card className="bg-error/5 border border-error/20 p-4 rounded-2xl">
          <div className="flex items-center justify-between gap-4">
            <p className="text-error text-sm font-medium">{error}</p>
            <Button size="sm" variant="secondary" onClick={clearError} className="flex-shrink-0">
              <Icon name="close" size={16} />
            </Button>
          </div>
        </Card>
      )}

      {(isLoadingCategories || isLoadingProducts) && (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-12 h-12 rounded-2xl border-2 border-primary/30 border-t-primary animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Yükleniyor...</p>
        </div>
      )}

      {!isLoadingCategories && !isLoadingProducts && (
        <>
          {activeTab === 'products' && (
            <ProductsTab
              products={filteredProducts}
              productsByCategory={productsByCategory}
              categories={categories}
              selectedCategoryFilter={selectedCategoryFilter}
              onCategoryFilterChange={setSelectedCategoryFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onEditProduct={handleEditProduct}
              onDeleteProduct={handleDeleteProduct}
              onToggleAvailability={handleToggleAvailability}
              onUpdateStock={handleOpenStockModal}
              onCreateProduct={handleCreateProduct}
              onManageOptionGroups={setOptionGroupProduct}
            />
          )}

          {activeTab === 'modifiers' && (
            <ModifiersTab
              onGroupCreated={() => {
                setToast({ message: 'Yan ürün grubu oluşturuldu', type: 'success' });
              }}
              onGroupDuplicated={() => {
                setToast({ message: 'Yan ürün grubu kopyalandı', type: 'success' });
              }}
            />
          )}

          {activeTab === 'categories' && (
            <CategoriesTab
              categories={categories}
              products={products}
              onCreateCategory={handleCreateCategory}
              onEditCategory={handleEditCategory}
              onDeleteCategory={handleDeleteCategoryClick}
              onCreateProduct={handleCreateProduct}
            />
          )}

        </>
      )}

      {isCategoryModalOpen && (
        <CategoryFormModal
          isOpen={isCategoryModalOpen}
          onClose={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          onSuccess={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
            loadCategories();
            setToast({
              message: selectedCategory ? 'Kategori güncellendi' : 'Kategori oluşturuldu',
              type: 'success',
            });
          }}
        />
      )}

      {isProductModalOpen && (
        <ProductFormModal
          isOpen={isProductModalOpen}
          onClose={() => {
            setIsProductModalOpen(false);
            setSelectedProduct(null);
            setSelectedCategory(null);
          }}
          product={selectedProduct}
          categoryId={selectedCategory?.id || selectedProduct?.categoryId || ''}
          onSuccess={() => {
            setIsProductModalOpen(false);
            setSelectedProduct(null);
            setSelectedCategory(null);
            loadProducts();
            setToast({
              message: selectedProduct ? 'Ürün güncellendi' : 'Ürün oluşturuldu',
              type: 'success',
            });
          }}
        />
      )}

      {isStockModalOpen && stockProduct && (
        <StockUpdateModal
          isOpen={isStockModalOpen}
          productName={stockProduct.name}
          currentStock={stockProduct.stockQuantity ?? null}
          currentThreshold={stockProduct.lowStockThreshold ?? null}
          onConfirm={handleUpdateStock}
          onClose={() => {
            setIsStockModalOpen(false);
            setStockProduct(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!categoryToDelete}
        title="Kategoriyi sil"
        description="Bu kategoriyi silmek istediğinize emin misiniz? Kategori içindeki tüm ürünler de silinecektir."
        confirmText="Sil"
        cancelText="Vazgeç"
        variant="danger"
        isConfirmLoading={isDeleteCategoryLoading}
        onConfirm={handleDeleteCategoryConfirm}
        onClose={() => setCategoryToDelete(null)}
      />

      {/* Option Group slide-over panel */}
      {optionGroupProduct && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOptionGroupProduct(null)}
          />
          <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Porsiyon & Seçenek Grupları</h2>
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{optionGroupProduct.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setOptionGroupProduct(null)}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Icon name="close" size={20} />
              </button>
            </div>
            <div className="flex-1 p-6">
              <OptionGroupManagement productId={optionGroupProduct.id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ProductsTabProps {
  products: Product[];
  productsByCategory: Array<{ category: Category; products: Product[] }>;
  categories: Category[];
  selectedCategoryFilter: string;
  onCategoryFilterChange: (categoryId: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (productId: string) => void;
  onToggleAvailability: (productId: string, isAvailable: boolean) => void;
  onUpdateStock: (product: Product) => void;
  onCreateProduct: (categoryId?: string) => void;
  onManageOptionGroups: (product: Product) => void;
}

const ProductsTab: React.FC<ProductsTabProps> = ({
  products,
  productsByCategory,
  categories,
  selectedCategoryFilter,
  onCategoryFilterChange,
  searchQuery,
  onSearchChange,
  onEditProduct,
  onDeleteProduct,
  onToggleAvailability,
  onUpdateStock,
  onCreateProduct,
  onManageOptionGroups,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative">
          <Icon
            name="search"
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <Input
            type="text"
            placeholder="Ürün ara..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-11 pr-10 py-2.5 rounded-2xl border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Temizle"
            >
              <Icon name="close" size={18} />
            </button>
          )}
        </div>
        <Select
          value={selectedCategoryFilter}
          onChange={(value) => onCategoryFilterChange(String(value))}
          placeholder="Tüm Kategoriler"
          options={[
            { value: 'all', label: 'Tüm Kategoriler' },
            ...categories.map((cat) => ({
              value: cat.id,
              label: cat.name,
            })),
          ]}
          size="sm"
          className="w-full sm:w-auto sm:min-w-[200px] rounded-2xl border-gray-200"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => onCreateProduct()}
          className="w-full sm:w-auto sm:ml-auto rounded-xl font-semibold shadow-sm hover:shadow-md transition-shadow"
        >
          <Icon name="plus" size={18} className="mr-2" />
          Yeni Ürün
        </Button>
      </div>

      {products.length === 0 ? (
        <Card padding="lg" className="text-center">
          <div className="py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="cube" size={32} className="text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium mb-1">
              {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz ürün eklenmemiş'}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {searchQuery ? 'Farklı anahtar kelimeler deneyin.' : 'İlk ürününüzü ekleyerek menünüzü oluşturmaya başlayın.'}
            </p>
            {!searchQuery && (
              <Button variant="primary" size="sm" onClick={() => onCreateProduct()} className="rounded-xl">
                <Icon name="plus" size={18} className="mr-2" />
                İlk Ürünü Ekle
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-8">
          {productsByCategory.length > 0 ? (
            productsByCategory.map(({ category, products: categoryProducts }) => (
              <div key={category.id} className="space-y-4">
                <div className="flex items-center gap-3 px-1">
                  {category.imageUrl && (
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200 bg-gray-50 shadow-sm">
                      <img
                        src={category.imageUrl}
                        alt={category.name}
                        className="w-full h-full object-contain object-center"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-gray-900 truncate">{category.name}</h3>
                    <p className="text-sm text-gray-500">{categoryProducts.length} ürün</p>
                  </div>
                  <Badge variant="secondary" className="rounded-lg font-medium">
                    {categoryProducts.length}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {categoryProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onEdit={() => onEditProduct(product)}
                      onDelete={() => onDeleteProduct(product.id)}
                      onToggleAvailability={() => onToggleAvailability(product.id, product.isAvailable)}
                      onUpdateStock={() => onUpdateStock(product)}
                      onManageOptionGroups={() => onManageOptionGroups(product)}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => onEditProduct(product)}
                  onDelete={() => onDeleteProduct(product.id)}
                  onToggleAvailability={() => onToggleAvailability(product.id, product.isAvailable)}
                  onUpdateStock={() => onUpdateStock(product)}
                  onManageOptionGroups={() => onManageOptionGroups(product)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ModifiersTabProps {
  onGroupCreated?: (group: any) => void;
  onGroupDuplicated?: (group: any) => void;
}

const ModifiersTab: React.FC<ModifiersTabProps> = ({ onGroupCreated, onGroupDuplicated }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Yan Ürün Grupları</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Sos seçimi, ekstra malzemeler gibi birden fazla üründe kullanılacak grupları oluşturun
        </p>
      </div>
      <ModifierGroupManagement
        onGroupCreated={onGroupCreated}
        onGroupDuplicated={onGroupDuplicated}
      />
    </div>
  );
};

interface CategoriesTabProps {
  categories: Category[];
  products: Product[];
  onCreateCategory: () => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (categoryId: string) => void;
  onCreateProduct: (categoryId: string) => void;
}

const CategoriesTab: React.FC<CategoriesTabProps> = ({
  categories,
  products,
  onCreateCategory,
  onEditCategory,
  onDeleteCategory,
  onCreateProduct,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Kategoriler</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Menü kategorilerinizi düzenleyin, sıralayın ve ürün ekleyin
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onCreateCategory}
          className="rounded-xl font-semibold shadow-sm w-full sm:w-auto"
        >
          <Icon name="plus" size={18} className="mr-2" />
          Yeni Kategori
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card padding="lg" className="text-center">
          <div className="py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="folder" size={32} className="text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium mb-1">Henüz kategori eklenmemiş</p>
            <p className="text-sm text-gray-500 mb-6">
              Kategoriler menünüzü gruplamanıza yardımcı olur (örn. Başlangıçlar, Ana Yemekler, İçecekler).
            </p>
            <Button variant="primary" size="sm" onClick={onCreateCategory} className="rounded-xl">
              <Icon name="plus" size={18} className="mr-2" />
              İlk Kategoriyi Ekle
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map((category) => {
            const categoryProducts = products.filter((p) => p.categoryId === category.id);
            return (
              <Card key={category.id} padding="none" className="overflow-hidden transition-all duration-300 hover:shadow-card-hover group">
                {category.imageUrl ? (
                  <div className="w-full aspect-[4/3] rounded-t-2xl overflow-hidden bg-gray-50 flex-shrink-0">
                    <img
                      src={category.imageUrl}
                      alt={category.name}
                      className="w-full h-full object-contain object-center"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] rounded-t-2xl bg-gray-100 flex items-center justify-center">
                    <Icon name="folder" size={40} className="text-gray-300" />
                  </div>
                )}
                <div className="p-4">
                  <h4 className="font-bold text-gray-900 mb-1 line-clamp-1">{category.name}</h4>
                  {category.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{category.description}</p>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="rounded-lg font-medium">
                      {categoryProducts.length} ürün
                    </Badge>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => onEditCategory(category)}
                      className="p-2.5 rounded-xl text-gray-600 hover:bg-primary/10 hover:text-primary transition-colors"
                      title="Düzenle"
                    >
                      <Icon name="pencil" size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onCreateProduct(category.id)}
                      className="p-2.5 rounded-xl text-gray-600 hover:bg-primary/10 hover:text-primary transition-colors"
                      title="Bu kategoriye ürün ekle"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteCategory(category.id)}
                      className="p-2.5 rounded-xl text-gray-500 hover:bg-error/10 hover:text-error transition-colors"
                      title="Kategoriyi sil"
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ProductCardProps {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  onToggleAvailability: () => void;
  onUpdateStock: () => void;
  onManageOptionGroups: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onEdit,
  onDelete,
  onToggleAvailability,
  onUpdateStock,
  onManageOptionGroups,
}) => {
  const isLowStock =
    product.stockQuantity !== null &&
    product.stockQuantity <= (product.lowStockThreshold || 10);

  return (
    <Card
      padding="none"
      className={cn(
        'relative overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-card-hover',
        !product.isAvailable && 'opacity-80',
      )}
    >
      <div className="flex flex-col h-full">
        {product.imageUrl ? (
          <div className="relative w-full aspect-[4/3] rounded-t-2xl overflow-hidden bg-gray-50 flex-shrink-0">
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full h-full object-contain object-center"
            />
            {!product.isAvailable && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px]">
                <span className="px-3 py-1.5 rounded-lg bg-error text-white text-xs font-semibold">
                  Tükendi
                </span>
              </div>
            )}
            {isLowStock && product.isAvailable && (
              <div className="absolute top-2 right-2">
                <span className="px-2.5 py-1 rounded-lg bg-warning/90 text-white text-xs font-semibold shadow-sm">
                  Düşük stok
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full aspect-[4/3] rounded-t-2xl bg-gray-100 flex-shrink-0 flex items-center justify-center">
            <Icon name="image" size={32} className="text-gray-300" />
          </div>
        )}

        <div className="flex flex-col flex-1 min-h-0 p-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-bold text-gray-900 text-sm leading-snug flex-1 line-clamp-2">
              {product.name}
            </h4>
            <Badge
              variant={product.isAvailable ? 'success' : 'danger'}
              className="flex-shrink-0 text-xs rounded-lg"
            >
              {product.isAvailable ? 'Açık' : 'Kapalı'}
            </Badge>
          </div>

          {product.description && (
            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-2">
              {product.description}
            </p>
          )}

          <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
            <span className="font-bold text-primary text-base">
              {formatCurrency(product.price)}
            </span>
            {product.calories && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg font-medium">
                {product.calories} kcal
              </span>
            )}
          </div>

          {product.stockQuantity !== null && (
            <div
              className={cn(
                'mt-2 text-xs px-3 py-2 rounded-xl flex items-center justify-between font-medium',
                isLowStock ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success',
              )}
            >
              <span>Stok</span>
              <span>{product.stockQuantity} adet</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onManageOptionGroups}
              className="p-2.5 rounded-xl text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              title="Porsiyon / Seçenek grupları"
            >
              <Icon name="adjustments" size={16} />
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="p-2.5 rounded-xl text-gray-600 hover:bg-primary/10 hover:text-primary transition-colors"
              title="Düzenle"
            >
              <Icon name="pencil" size={16} />
            </button>
            <button
              type="button"
              onClick={onToggleAvailability}
              className={cn(
                'p-2.5 rounded-xl transition-colors',
                product.isAvailable
                  ? 'text-gray-600 hover:bg-warning/10 hover:text-warning'
                  : 'text-gray-600 hover:bg-success/10 hover:text-success',
              )}
              title={product.isAvailable ? 'Kullanılamaz yap' : 'Kullanılabilir yap'}
            >
              <Icon name={product.isAvailable ? 'close' : 'check'} size={16} />
            </button>
            {product.stockQuantity !== null && (
              <button
                type="button"
                onClick={onUpdateStock}
                className="p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-primary transition-colors"
                title="Stok güncelle"
              >
                <Icon name="archive" size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={onDelete}
              className="p-2.5 rounded-xl text-gray-600 hover:bg-error/10 hover:text-error transition-colors"
              title="Sil"
            >
              <Icon name="trash" size={16} />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};


export default MenuTab;
